import { axiosInstance } from './api';

// Shift-dashboard reads are polled and gate the screen. These aggregations are
// genuinely slow on big zones (all racks/tables × occupancy history) — a too-
// tight budget aborts a still-valid response, errors, and retries (making the
// load SLOWER and flashing "couldn't load"). 30s lets the first attempt finish;
// the FE only surfaces an error once SWR has actually stopped retrying.
const DASH_TIMEOUT = 30_000;
const LAYOUT_TIMEOUT = 30_000;

// ── Cleanliness of tables (table_control plugin) ───────────────────────────

/**
 * F5 fields shared by every dashboard sample: the FULL source frame (not the
 * crop) when its image is still stored — blank/pruned for most frames → null —
 * plus the target outline(s) in normalized 0..1 image coords
 * (PolygonArea.direction_vectors). With both set the FE can draw the full frame
 * + outlined target behind a flag, falling back to the crop otherwise.
 */
export interface FullFrameProof {
  frame_image_url?: string | null;
  polygons?: number[][][] | null;
  /** ISO time of the full frame — the camera's FRESHEST frame, independent of
   *  the (possibly stale) crop. Drives the timestamp chip in full-frame view. */
  frame_captured_at?: string | null;
}

/** State from the table automaton, shared with the history strips. */
export type TableState = 'free' | 'occupied' | 'dirty' | 'moved' | 'shifted' | 'unknown' | 'no_data';

export interface DirtyTableSample extends FullFrameProof {
  id: number | string;
  name: string;
  dirty: boolean;
  /** Missing state is displayed as unavailable. */
  state?: TableState;
  recheck_pending?: boolean;
  state_observed_at?: string | null;
  state_started_at?: string | null;
  spoiled_reason?: 'several_tables' | 'obstructed' | 'glare' | null;
  spoiled_since?: string | null;
  image_url: string | null;
  /** ISO timestamp of the frame the photo was captured from. */
  captured_at?: string | null;
  /** Camera the table's polygon lives on — physical-cluster ordering key. */
  camera_id?: number | string | null;
}

export interface TablesSummary {
  total: number;
  dirty_count: number;
  /** False when the zone produced NO frames this (zone-local) day — the
   *  statuses are stale; the FE greys the panel out. Absent on pre-deploy BE. */
  has_frames_today?: boolean;
  latest_frame_at?: string | null;
  sample: DirtyTableSample[];
  /**
   * Representative "proof it's OK" photos of non-dirty tables — mirrors the
   * layout/queue cards which always show photos of the monitored units, not
   * just the problem ones. Optional for backward-compat with pre-deploy BE.
   */
  clean_sample?: DirtyTableSample[];
}

// `limit` caps how many dirty/clean tables are returned. Default (omitted) keeps
// the BE's historical 6 for the Analytics snapshot strip; the worker dashboard
// passes 0 to get ALL tables and paginate them itself.
export const getTablesSummary = (zoneId: string, opts?: { limit?: number }) =>
  axiosInstance.get<TablesSummary>('/table_control/summary/', {
    params: { zone_id: zoneId, ...(opts?.limit !== undefined ? { limit: opts.limit } : {}) },
    timeout: DASH_TIMEOUT,
  });

/**
 * Worker pressed "Убрали" — backend resolves the alert and drops the table
 * from the dashboard until the next pipeline cycle re-detects dirt.
 */
export const rescanTable = (tableId: number | string) =>
  axiosInstance.post<{ status: string }>(`/table_control/table/${tableId}/rescan/`);

// ── Layout health (empty_shelf plugin) ──────────────────────────────────────

export type LayoutHealth = 'green' | 'yellow' | 'red';

export interface RackSample extends FullFrameProof {
  id: number | string;
  name: string;
  /** Server-side category label, e.g. "Выпечка", "Готовая еда". null when not annotated. */
  rack_type: string | null;
  status: 'green' | 'yellow' | 'orange' | 'grey' | 'unknown';
  raw_status: string | null;
  image_url: string | null;
  /** ISO timestamp of the frame the photo was captured from. */
  captured_at?: string | null;
  /** ISO timestamp the current non-green issue started (alerting frame). The
   *  photo itself is always the LATEST; this drives the "issue since HH:MM"
   *  suffix on the status dot. null when the rack is OK. */
  issue_since?: string | null;
  /** Стеллаж сейчас заслонён (человек перед ним или кадр непригоден): статус
   *  серый нейтральный, фото — последний кадр, где стеллаж был виден (AI12.5). */
  occluded?: boolean;
  occluded_since?: string | null;
  /** Сотрудник нажал «Поправили», камера ещё не подтвердила (Алексей 20.10 #1639). */
  fixed_pending?: boolean;
  /** Причины статуса (#2088): по полкам — низ полки в координатах кадра, заполненность и порог
   *  с того же чтения, что дало статус. */
  shelves?: ShelfMark[] | null;
}

export interface ShelfMark {
  name: string;
  /** Две точки нижней границы полки, 0…1 кадра. */
  bottom: number[][];
  /** Все вершины полки, 0…1 кадра (#3188). */
  points?: number[][];
  pct: number;
  thr: number;
  count: number | null;
  low: boolean;
}

export interface LayoutSummary {
  health: LayoutHealth;
  total: number;
  green_count: number;
  yellow_count: number;
  orange_count: number;
  grey_count: number;
  red_count: number;
  unknown_count: number;
  /** False when the zone produced NO frames this (zone-local) day — the
   *  statuses are stale; the FE greys the panel out. Absent on pre-deploy BE. */
  has_frames_today?: boolean;
  latest_frame_at?: string | null;
  sample: RackSample[];
}

/** «Поправили» у стеллажа на дашборде (Алексей 20.10 #1639): чтения модели до нажатия
 *  больше не держат тревогу; если камера и после снова видит проблему — бейдж вернётся. */
export const markRackFixed = (rackId: number | string) =>
  axiosInstance.post<{ status: string }>(`/empty_shelf/rack/${rackId}/fixed/`);

export const getLayoutSummary = (zoneId: string) =>
  axiosInstance.get<LayoutSummary>('/empty_shelf/zone-summary/', {
    params: { zone_id: zoneId },
    timeout: LAYOUT_TIMEOUT,
  });

// ── Queue snapshot (checkout_control plugin) ────────────────────────────────

export interface QueueSample extends FullFrameProof {
  id: number | string;
  name: string;
  current: number | null;
  max_allowed: number;
  status: 'red' | 'yellow' | 'green' | 'unknown';
  image_url: string | null;
  /** ISO timestamp of the frame the photo was captured from. */
  captured_at?: string | null;
}

export interface QueueSummary {
  configured: boolean;
  health: LayoutHealth;
  total: number;
  over_limit_count: number;
  at_limit_count: number;
  /** False when the zone produced NO frames this (zone-local) day — the
   *  statuses are stale; the FE greys the panel out. Absent on pre-deploy BE. */
  has_frames_today?: boolean;
  latest_frame_at?: string | null;
  queues: QueueSample[];
}

export const getQueueSummary = (zoneId: string) =>
  axiosInstance.get<QueueSummary>('/checkout_control/zone-summary/', {
    params: { zone_id: zoneId },
    timeout: DASH_TIMEOUT,
  });

/** Очередь на дашборде смены (Алексей #2045): вспыхнувшая проблема держится на экране не
 *  меньше QUEUE_HOLD_MS — раньше очередь появлялась на один опрос и пропадала. Пока держим,
 *  показываем последний «проблемный» снимок этой очереди (фото и число) и пересчитываем счётчики. */
export const QUEUE_HOLD_MS = 60_000;
const queueHeld = new Map<string, { until: number; q: QueueSample }>();
const isQueueProblem = (q: QueueSample) => q.status === 'red' || q.status === 'yellow';
export function holdQueueProblems(zoneId: string, data: QueueSummary, now = Date.now()): QueueSummary {
  if (!data.configured || data.has_frames_today === false) return data;
  let changed = false;
  const queues = data.queues.map(q => {
    const key = `${zoneId}:${q.id}`;
    const h = queueHeld.get(key);
    if (isQueueProblem(q)) {
      queueHeld.set(key, { until: h && h.until > now ? h.until : now + QUEUE_HOLD_MS, q });
      return q;
    }
    if (h && h.until > now) { changed = true; return h.q; }
    queueHeld.delete(key);
    return q;
  });
  if (!changed) return data;
  const over = queues.filter(q => q.status === 'red').length;
  const at = queues.filter(q => q.status === 'yellow').length;
  return { ...data, queues, over_limit_count: over, at_limit_count: at, health: over ? 'red' : at ? 'yellow' : 'green' };
}

// ── Zone open/closed status ──────────────────────────────────────────────────

export interface ZoneOpenStatus {
  is_open: boolean;
  /** Next opening time as 'HH:MM' in the zone's local timezone, or null. */
  opens_at: string | null;
}

export const getZoneOpenStatus = (zoneId: string) =>
  axiosInstance.get<ZoneOpenStatus>('/empty_shelf/zone-open-status/', {
    params: { zone_id: zoneId },
    timeout: DASH_TIMEOUT,
  });
